/*
 * The fleet's workspace switch (`tiketo-cms` @ `c4387239`,
 * `modules-user-management/src/frontend/components/user-management`).
 *
 * Two deviations, both from something that is missing rather than from an
 * opinion; the rest is the fleet's file, `if (!activeWorkspace) return null`
 * above `useMemo` included:
 *
 *  - `useTranslation` is ours (`hooks/useTranslation`), a closed set of English
 *    strings under the fleet's keys. This console has no i18next.
 *  - the "Add workspace" row opens `CreateWorkspaceModal`, ours rather than the
 *    fleet's — the fleet's own modal is written against tiketo's mutations,
 *    which are the one thing this copy does not take (design note §6). It was
 *    `disabled` because nothing in this API created a workspace; that changed
 *    2026-09-15 (`POST /v1/workspaces`, design note §6, variant A — a person's
 *    own additional workspaces, no sharing, no invites; Jakub's go-ahead,
 *    Slack thread `1788864063.015979`), so the row is live and the separator
 *    stays exactly where the fleet has it.
 *
 * The fleet's own file has the early return *above* `useMemo` — a conditional
 * hook, harmless there only because `activeWorkspace` was null on no render
 * it ever reached. `sitebrewhq/sitebrew-api#252` (2026-09-25) broke that: an
 * account can now land on `NoWorkspaceScreen` — `activeWorkspace` null,
 * `WorkspaceSwitch` still mounted in the persisting sidebar — and leave it
 * for a workspace of its own without a remount, the exact moment the guard
 * used to rule out. So the `useMemo` moved above the return, here only: same
 * fleet logic, called every render instead of conditionally, which is the
 * ordinary Rules-of-Hooks fix and not a rewrite of what it computes.
 */
import React, { useState, useMemo } from "react";
import { ChevronsUpDown, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { useWorkspaces, type Workspace } from "../../contexts/WorkspaceContext";
import { useTranslation } from "../../hooks/useTranslation";
import { SearchInput } from "../ui/search-input";
import { BrandMark } from "../brand-mark";
import { CreateWorkspaceModal } from "./CreateWorkspaceModal";

interface SidebarComponents {
  SidebarMenu?: React.ComponentType<{ children: React.ReactNode }>;
  SidebarMenuItem?: React.ComponentType<{ children: React.ReactNode }>;
  SidebarMenuButton?: React.ComponentType<any>;
}

interface WorkspaceSwitchProps {
  sidebarComponents?: SidebarComponents;
}

export const WorkspaceSwitch: React.FC<WorkspaceSwitchProps> = ({
  sidebarComponents = {}
}) => {
  const {
    SidebarMenu = ({ children }) => <div>{children}</div>,
    SidebarMenuItem = ({ children }) => <div>{children}</div>,
    SidebarMenuButton = ({ children, className, ...props }: any) => (
      <button className={className} {...props}>{children}</button>
    ),
  } = sidebarComponents;
  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspaces();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Filter workspaces by search query (already sorted on backend)
  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery.trim()) {
      return workspaces;
    }

    const query = searchQuery.toLowerCase();
    return workspaces.filter((ws) =>
      ws.name.toLowerCase().includes(query)
    );
  }, [workspaces, searchQuery]);

  if (!activeWorkspace) return null;

  const handleWorkspaceSwitch = (workspace: Workspace) => {
    // Only switch if it's different from current active workspace
    if (workspace.id !== activeWorkspace.id) {
      switchWorkspace(workspace);
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <activeWorkspace.logo className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {activeWorkspace.name}
                </span>
                <span className="truncate text-xs">
                  {activeWorkspace.plan}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 rounded-lg"
            align="start"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {t("workspaceSwitch.selectWorkspace")}
            </DropdownMenuLabel>
            <div className="px-2 pb-2" onKeyDown={(e) => e.stopPropagation()}>
              <div onClick={(e) => e.stopPropagation()}>
                <SearchInput
                  placeholder={t("workspaceSwitch.searchWorkspaces")}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  className="h-9"
                  autoFocus
                />
              </div>
            </div>
            {filteredWorkspaces.length === 0 ? (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                {t("workspaceSwitch.noWorkspacesFound")}
              </div>
            ) : (
              filteredWorkspaces.map((ws: Workspace) => (
              <DropdownMenuItem
                key={ws.id} // Use id instead of name for better uniqueness
                onClick={() => handleWorkspaceSwitch(ws)}
                className={`gap-2 p-2 ${
                  ws.id === activeWorkspace.id
                    ? "bg-accent text-accent-foreground"
                    : ""
                }`}
              >
                <div className="flex size-6 items-center justify-center rounded-md border">
                  <ws.logo className="size-3.5 shrink-0" />
                </div>
                {ws.name}
                {ws.id === activeWorkspace.id && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {t("workspaceSwitch.current", { name: ws.name })}
                  </span>
                )}
              </DropdownMenuItem>
            ))
            )}
            <DropdownMenuSeparator />
            {/*
              The fleet's row, now live: `POST /v1/workspaces` exists
              (2026-09-15, see the header above), so this opens
              `CreateWorkspaceModal` instead of staying `disabled`.
            */}
            <DropdownMenuItem onClick={() => setCreateModalOpen(true)} className="gap-2 p-2">
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <Plus className="size-4" />
              </div>
              <div className="font-medium">{t("workspaceSwitch.addWorkspace")}</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <CreateWorkspaceModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onCreated={(workspace) =>
            switchWorkspace({
              id: workspace.id,
              name: workspace.name,
              logo: BrandMark,
              plan: workspace.role,
            })
          }
        />
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
